/* Innate section navigator (2026-07-31)
 * ------------------------------------------------------------------
 * A slim vertical rail that lists the sections of the current page,
 * highlights the one you are viewing, and scrolls to a section on click.
 *
 * Sections are detected automatically from the page's own Shopify sections,
 * so it works on every template without per-page setup and follows any
 * reordering done in the theme editor.
 *
 * Labels: section headings on this site are written as sentences ("Every
 * table starts in a forest we can name."), which do not read as navigation.
 * LABEL_OVERRIDES maps the known long headings to short labels; anything
 * unmatched falls back to a shortening rule. Add a line to the map to fix a
 * label — no other change needed.
 *
 * Desktop only: the rail is hidden below 1100px in CSS, where there is no
 * room beside the content and no hover to drive the proximity effect.
 * ------------------------------------------------------------------ */
(function () {
  'use strict';

  var MIN_SECTIONS = 3;
  var MAX_LABEL = 22;
  var PROXIMITY = 110; // px — how far the pointer influence reaches
  var MAX_SHIFT = 10; // px — how far an item slides toward the pointer
  var SMOOTHING = 110; // ms — easing time constant

  /* Pages where the rail adds nothing — too few real sections. */
  var PAGE_EXCLUDE = ['/pages/maintaining-your-table'];

  /* Headings that are not navigation destinations (newsletter sign-up,
     near-duplicate section intros). Matched on a lowercase prefix. */
  var LABEL_EXCLUDE = [
    'subscribe',
    'built around the project',
    'start with the product',
    'the same material language'
  ];

  /* Short, Title Case labels for each heading, matched on a lowercase prefix.
     Written out in full rather than derived, because the site's headings are
     sentences. Several headings deliberately share a label — the duplicate
     check then collapses them into a single entry (e.g. the first three
     contact headings become one "Overview"). */
  var LABEL_OVERRIDES = {
    // Homepage
    'native timber furniture': 'Overview',
    'hard to picture': 'Design In 3D',
    'what are you making': 'Categories',
    'every table starts in a forest': 'Our Timber',
    'what you can expect': 'What To Expect',
    'tell us what you are after': 'Enquire',

    // Dining tables
    'dining tables made to order': 'Overview',
    'browse dining table styles': 'Styles',
    'choosing a dining table': 'Buying Guide',
    'tabletop shapes': 'Shapes',
    'table size guide': 'Size Guide',
    'timber and colour': 'Timber & Colour',
    'start with a rough table brief': 'Enquire',
    'dining table faqs': 'FAQs',
    'looking for something else': 'More',

    // Benchtops
    'plan your timber benchtop': 'Overview',
    'a useful brief before we talk': 'Your Brief',
    'wooden kitchen benchtops': 'Where It Works',
    'three native timbers': 'Timbers',
    'before we make it': 'How It Works',
    'made in our christchurch workshop': 'Our Workshop',
    'common benchtop questions': 'FAQs',
    'ready to send a benchtop brief': 'Enquire',

    // Outdoor
    'custom outdoor dining tables': 'Overview',
    'start with the setting': 'Choose A Surface',
    'not throwaway outdoor': 'Built To Last',
    'common questions before ordering': 'FAQs',
    'choose an outdoor dining table': 'Browse',
    'send the space': 'Enquire',

    // Our story
    'made here, made to last': 'Overview',
    'furniture for homes': 'What We Make',
    'a practical custom workshop': 'The Workshop',
    'how we choose to build': 'Guidance',
    'the work starts with a conversation': 'The Process',
    'made for real homes': 'Reviews',
    'start with the room, not a fin': 'Get Started',

    // Contact — first three headings collapse into one entry
    'start with the room.': 'Overview',
    'people who know the work': 'Overview',
    'talk to innate': 'Overview',
    'choosing timber or finish': 'Timber Advice',
    'what do you need help with': 'How We Help',
    'book a showroom visit': 'Showroom',
    'tell us what you already know': 'Enquire',

    // FAQ page
    'frequently asked questions': 'Overview',
    'we\u2019re here to answer': 'Overview',
    "we're here to answer": 'Overview',
    'general information': 'General',
    'ordering & customization': 'Ordering',
    'pricing & payment': 'Pricing',
    'shipping & delivery': 'Delivery',
    'production & lead times': 'Lead Times',
    'care & maintenance': 'Care',
    'other questions': 'Still Stuck?',

    // Boardroom tables — "built for the way" and "built around your space" merge
    'custom timber boardroom tables': 'Overview',
    'make it yours': 'Customise',
    'three shapes': 'Shapes',
    'recent boardroom table directions': 'Recent Work',
    'built for the way your team meets': 'Built For You',
    'built around your space': 'Built For You',
    'three steps from brief to delivery': 'How It Works',
    'frequently asked': 'FAQs',
    'get a boardroom table quote': 'Get A Quote',

    // Commercial
    'custom commercial furniture': 'Overview',
    'selected hospitality and commercial work': 'Recent Work',
    'start with the type of project': 'Project Types',
    'good for projects where standard': 'Where We Fit',
    'commercial furniture made around real briefs': 'Our Approach',
    'you do not need final drawings': 'Getting Started',
    'materials with a story': 'Materials',
    'send us the commercial brief': 'Enquire',

    // Home & living
    'furniture for the rooms you use': 'Overview',
    'strong starting points': 'Popular Pieces',
    'made-to-order storage': 'Storage & Desks',
    'solid timber tables for living rooms': 'Living Tables',
    'kitchen pieces and smaller timber goods': 'Kitchen',
    'bar leaners for indoor': 'Bar Leaners',
    'compare colour and grain at home': 'Samples',
    'useful details before you enquire': 'Good To Know',
    'need a different size': 'Enquire',

    // Materials
    'materials we know, trust': 'Overview',
    'choose timber by character': 'Timbers',
    'a local industrial material': 'New Zealand Steel',
    'finishes that protect the timber': 'Finishes',
    'a material decision should feel': 'How To Choose',
    'useful things to know before choosing': 'FAQs',
    'see the materials in person': 'Samples',

    // Sustainability
    'sustainability': 'Overview',
    "what does 'sustainable furniture'": 'What It Means',
    'tree planting': 'Tree Planting',
    'keep it local': 'Keep It Local',

    // Delivery & installation
    'delivery planned before we build': 'Overview',
    'delivery is part of the job': 'Our Approach',
    'large furniture needs a plan': 'Planning',
    'what we may ask you': 'What We Ask',
    'for commercial and trade projects': 'Commercial',
    'what delivery costs': 'Costs',
    'built in christchurch, delivered': 'Where We Deliver',
    'delivery faqs': 'FAQs',
    'send us the room and destination': 'Enquire',

    // Hospitality
    'custom hospitality furniture': 'Overview',
    'furniture for places people return to': 'Why Innate',
    'hospitality tables, bar leaners': 'What We Make',
    'common hospitality table sizes': 'Table Sizes',
    'materials chosen around the job': 'Materials',
    'built for daily service': 'Built To Last',
    'how a hospitality project works': 'Our Process',
    'hospitality and commercial work in use': 'Recent Work',
    'planning a hospitality space': 'Enquire'
  };

  // Capitalise each word, leaving the rest of the word alone so acronyms
  // such as NZ and FAQs survive.
  function titleCase(text) {
    return text.replace(/\b([a-z])/g, function (m, c) {
      return c.toUpperCase();
    });
  }

  function isExcluded(key) {
    for (var i = 0; i < LABEL_EXCLUDE.length; i++) {
      if (key.indexOf(LABEL_EXCLUDE[i]) === 0) return true;
    }
    return false;
  }

  function shorten(text) {
    var clean = text.replace(/\s+/g, ' ').trim();
    var key = clean.toLowerCase();

    for (var prefix in LABEL_OVERRIDES) {
      if (key.indexOf(prefix) === 0) return LABEL_OVERRIDES[prefix];
    }

    // Fall back: first clause, trimmed of trailing punctuation, Title Cased.
    var firstClause = clean.split(/[.?—–:]/)[0].trim();
    if (firstClause.length <= MAX_LABEL) return titleCase(firstClause);

    var cut = firstClause.slice(0, MAX_LABEL);
    var lastSpace = cut.lastIndexOf(' ');
    return titleCase((lastSpace > 8 ? cut.slice(0, lastSpace) : cut).trim());
  }

  /* Collect headings rather than Shopify sections.
   *
   * Several pages (outdoor, our-story, contact) are built as a single large
   * section containing many visual blocks, so looking at section level found
   * only one or two headings and the rail never appeared. Headings sit at the
   * right granularity on every template and double as the scroll target. */
  function collectSections() {
    var scope = document.querySelector('.layout') || document.body;
    var headings = scope.querySelectorAll('h1, h2');
    var found = [];
    var seen = {};

    Array.prototype.forEach.call(headings, function (heading) {
      // Header and footer groups are chrome, not page content.
      if (heading.closest('.shopify-section-group')) return;
      if (heading.closest('.footer')) return;
      // Skip headings inside collapsed answers or cards, which aren't landmarks.
      if (heading.closest('details')) return;

      var text = heading.textContent.replace(/\s+/g, ' ').trim();
      if (text.length < 3) return;
      if (isExcluded(text.toLowerCase())) return;

      var label = shorten(text);
      if (seen[label]) return; // avoid duplicate entries
      seen[label] = true;

      found.push({ el: heading, label: label, full: text });
    });

    return found;
  }

  /* The theme pins a compact header once you scroll. At the top of a page it
   * is not pinned yet, so measuring "now" returns 0 — scrolling the full
   * distance then let the header engage and cover the destination, which read
   * as overshooting. Remember the pinned height instead and always reserve it. */
  var pinnedHeaderHeight = 110; // sensible default until first measured
  var CLEARANCE = 44; // breathing room below the header, tuned by eye

  function measurePinnedHeader() {
    var header =
      document.querySelector('.section-header .bar.fixed') ||
      document.querySelector('.header .fixed');
    if (!header) return;
    var pos = window.getComputedStyle(header).position;
    if (pos !== 'fixed' && pos !== 'sticky') return; // not pinned right now
    var height = Math.round(header.getBoundingClientRect().height);
    if (height > 0) pinnedHeaderHeight = height;
  }

  function headerOffset() {
    measurePinnedHeader();
    // Floor at 110: the header can be measured as 0 before it first pins, and
    // an under-measured offset leaves the destination tucked under the banner.
    return Math.max(pinnedHeaderHeight, 110);
  }

  function init() {
    if (PAGE_EXCLUDE.indexOf(window.location.pathname.replace(/\/$/, '')) !== -1) return;

    /* The blog index is a list of articles, not a page with sections to jump
       between — every h2 is an article title, so the rail just mirrored the
       list beside it. Matched on the body class rather than the path so it
       covers any blog, and so single article pages (.template-article), where
       jumping between headings is genuinely useful, keep theirs. */
    if (document.body.classList.contains('template-blog')) return;

    var sections = collectSections();
    if (sections.length < MIN_SECTIONS) return;

    var nav = document.createElement('nav');
    nav.className = 'innate-secnav';
    /* An id is needed for specificity: innate-theme-consistency.css styles
     * buttons via `body#outdoor :is(.btn, button, …) { … !important }`, and an
     * ID in that selector outranks any number of classes. */
    nav.id = 'innate-secnav';
    nav.setAttribute('aria-label', 'Sections on this page');

    /* Collapsed by default: at full width the rail overlaps page content as
     * you scroll. A small tab stays on the edge to open it. The list is only
     * open while someone is choosing a section — it closes when they pick
     * one, press anywhere else, scroll the page or press Escape — so it never
     * sits over the content while the page is being read. */
    var panel = document.createElement('div');
    panel.className = 'innate-secnav__panel';
    panel.id = 'innate-secnav-panel';

    var toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'innate-secnav__toggle';
    toggle.setAttribute('aria-controls', panel.id);
    toggle.innerHTML =
      '<span class="innate-secnav__toggle-icon" aria-hidden="true"></span>' +
      '<span class="innate-secnav__toggle-text">Sections</span>';

    /* The open state used to be remembered across pages, which reopened the
       list over the content on every page visited afterwards. Always start
       collapsed — don't reintroduce that — and clear the old stored value. */
    try {
      window.localStorage.removeItem('innate-secnav-open');
    } catch (e) {
      /* storage unavailable (private mode) — nothing to clear */
    }

    var open = false;
    var openedAtScrollY = 0;

    function setOpen(next) {
      open = next;
      if (open) openedAtScrollY = window.scrollY;
      nav.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      toggle.setAttribute('aria-label', open ? 'Hide section list' : 'Show section list');
    }

    /* The panel hides with visibility: hidden, so if keyboard focus is on one
       of its links, hand focus back to the tab rather than strand it on a
       control that has just disappeared. */
    function close() {
      if (!open) return;
      var focusInPanel = panel.contains(document.activeElement);
      setOpen(false);
      if (focusInPanel) toggle.focus({ preventScroll: true });
    }

    toggle.addEventListener('click', function () {
      setOpen(!open);
    });

    var list = document.createElement('ul');
    panel.appendChild(list);
    nav.appendChild(toggle);
    nav.appendChild(panel);
    setOpen(false);

    sections.forEach(function (section, index) {
      var item = document.createElement('li');
      item.className = 'innate-secnav__item';

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'innate-secnav__link';
      button.title = section.full;

      var tick = document.createElement('span');
      tick.className = 'innate-secnav__tick';
      tick.setAttribute('aria-hidden', 'true');

      var num = document.createElement('span');
      num.className = 'innate-secnav__index';
      num.setAttribute('aria-hidden', 'true');
      num.textContent = String(index + 1).padStart(2, '0');

      var label = document.createElement('span');
      label.className = 'innate-secnav__label';
      label.textContent = section.label;

      button.appendChild(tick);
      button.appendChild(num);
      button.appendChild(label);
      item.appendChild(button);
      list.appendChild(item);

      section.item = item;

      /* Reserve space for the pinned header via scroll-margin-top and let the
       * browser do the positioning. Computing a target manually raced with the
       * header pinning during the scroll, which landed slightly too far down. */
      section.el.style.scrollMarginTop = headerOffset() + CLEARANCE + 'px';

      button.addEventListener('click', function () {
        // Re-apply in case the header height changed since load (e.g. resize).
        section.el.style.scrollMarginTop = headerOffset() + CLEARANCE + 'px';
        // A section has been chosen, so the list has done its job. Close before
        // scrolling, so any focus move happens before the smooth scroll starts.
        close();
        section.el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    document.body.appendChild(nav);

    /* --- Dismissal ----------------------------------------------------
     * Must stay above the reduced-motion early return further down, or
     * visitors with reduced motion switched on would never get it. */
    var SCROLL_CLOSE = 40; // px — ignores incidental nudges, closes on a real scroll

    // A press anywhere outside the tab and list. Capture phase, so page
    // scripts that stop propagation can't hold the list open. Presses in the
    // gaps between pills count as outside: the rail is pointer-events: none
    // there, so they land on the page underneath.
    document.addEventListener('pointerdown', function (e) {
      if (open && !nav.contains(e.target)) close();
    }, true);

    // Scrolling the page means the customer is reading, not choosing. The
    // list has no scroll of its own, so wheeling over it scrolls the page too.
    window.addEventListener('scroll', function () {
      if (open && Math.abs(window.scrollY - openedAtScrollY) > SCROLL_CLOSE) close();
    }, { passive: true });

    // Escape, scoped to the navigator so it doesn't take the key from other
    // components such as the enquiry rail.
    nav.addEventListener('keydown', function (e) {
      if (open && e.key === 'Escape') {
        close();
        toggle.focus({ preventScroll: true });
      }
    });

    /* --- Active section tracking ------------------------------------- */
    var active = -1;
    function setActive(index) {
      if (index === active) return;
      active = index;
      sections.forEach(function (s, i) {
        s.item.classList.toggle('is-active', i === index);
        if (i === index) s.item.firstChild.setAttribute('aria-current', 'true');
        else s.item.firstChild.removeAttribute('aria-current');
      });
    }

    function updateActive() {
      /* The active section is the last one whose top has passed the reading
       * line (just below the sticky header). Falling back to index 0 means
       * the first section stays active until the second one arrives, which
       * is what you want at the top of a page. At the very bottom the last
       * section wins outright, so a short final section still registers. */
      var line = headerOffset() + 90;
      var best = 0;

      for (var i = 0; i < sections.length; i++) {
        if (sections[i].el.getBoundingClientRect().top <= line) best = i;
      }

      var atBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;
      if (atBottom) best = sections.length - 1;

      setActive(best);
    }

    updateActive();
    window.addEventListener('scroll', updateActive, { passive: true });
    window.addEventListener('resize', updateActive);

    /* --- Pointer proximity easing ------------------------------------ */
    var reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;

    var targets = sections.map(function () { return 0; });
    var current = sections.map(function () { return 0; });
    var raf = null;
    var last = 0;

    function frame(now) {
      var dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      var k = 1 - Math.exp(-dt / (SMOOTHING / 1000));
      var moving = false;

      for (var i = 0; i < sections.length; i++) {
        var target = targets[i];
        var next = current[i] + (target - current[i]) * k;
        var settled = Math.abs(target - next) < 0.002;
        current[i] = settled ? target : next;
        sections[i].item.style.setProperty('--effect', current[i].toFixed(3));
        if (!settled) moving = true;
      }

      raf = moving ? requestAnimationFrame(frame) : null;
    }

    function start() {
      if (raf != null) return;
      last = performance.now();
      raf = requestAnimationFrame(frame);
    }

    nav.addEventListener('pointermove', function (e) {
      var rect = list.getBoundingClientRect();
      var pointerY = e.clientY - rect.top;
      for (var i = 0; i < sections.length; i++) {
        var el = sections[i].item;
        var centre = el.offsetTop + el.offsetHeight / 2;
        var distance = Math.abs(pointerY - centre);
        var p = Math.max(0, 1 - distance / PROXIMITY);
        targets[i] = p * p * (3 - 2 * p); // smoothstep
      }
      start();
    });

    nav.addEventListener('pointerleave', function () {
      targets = targets.map(function () { return 0; });
      start();
    });

    nav.style.setProperty('--max-shift', MAX_SHIFT + 'px');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
